import {Plant} from "../navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {AuthDataService} from "../../services/auth/authData.service";
import {apsEnums} from "../../shared/dto/aps.enums";
import * as _ from "lodash";
import {apsCommon} from "../../shared/dto/aps.common";
import {Strings} from "../../shared/config/constant.module";

export interface CheckList extends apsViewModels.CheckListSummary {
  locationName: string;
  wareClass: string;
  isMandatory: boolean;
  isDisabled: boolean;
  isMandatoryFor(plantId: string, shopId: string, jobId: string): boolean;
  isDisabledFor(plantId: string, shopId: string, jobId: string): boolean;
  isOnlyDisabled(plantId: string, shopId: string, jobId: string): boolean;
  canChangeMandatory: boolean;
  canChangeDisabled: boolean;

  canEdit: boolean;
  canCopy: boolean;
  checkListGroupName: string;
}

export interface CheckItem extends apsViewModels.CheckItem {
  isFirst: boolean;
  isLast: boolean;
  dataTypeString: string;
  isMandatory: boolean;
  isDisabled: boolean;
  isMandatoryFor(plantId: string, shopId: string, jobId: string): boolean;
  isDisabledFor(plantId: string, shopId: string, jobId: string): boolean;
  isOnlyDisabled(plantId: string, shopId: string, jobId: string): boolean;
  canChangeMandatory: boolean;
  canChangeDisabled: boolean;
}

export interface Override {
  plantId: string;
  shopId: string;
  jobId: string;
  isMandatory: boolean;
  isDisabled: boolean;
}

export interface ExtendedHasOverrides extends HasOverrides {
  isMandatory: boolean;
  isDisabled: boolean;
  isMandatoryFor(plantId: string, shopId: string, jobId: string): boolean;
  isDisabledFor(plantId: string, shopId: string, jobId: string): boolean;
  isOnlyDisabled(plantId: string, shopId: string, jobId: string): boolean;
  canChangeMandatory: boolean;
  canChangeDisabled: boolean;
}

export interface HasOverrides {
  plantId: string;
  shopId: string;
  overrides: Override[];
}

export class CheckListCommon {
  private static shopAllSelection: apsViewModels.ShopNavigation = {
    id: null,
    name: 'All',
    defaultNumberOfGobs: 2,
    defaultNumberOfSections: 8,
    defaultPackPercentageTarget: 90,
    plantId: null,
    plantLocationId: null,
    hasLines: false
  };
  private static plantAllSelection: Plant = {
    id: null,
    name: 'All',
    shops: [CheckListCommon.shopAllSelection],
    tanks: [],
    plantLocationId: null,
    isDisabled: false,
    timeZoneId: Strings.DEFAULT_TIMEZONE,
    countRejectPercent: apsEnums.CountRejectPercent.PercentOfInspection,
    others: []
  };
  private static wareClassAllSelection: apsCommon.IdAndName = {
    id: null,
    name: 'All'
  };

  static getAllShopsItem(): apsViewModels.ShopNavigation {
    return CheckListCommon.shopAllSelection;
  }

  static getAllPlantsItem(): Plant {
    return CheckListCommon.plantAllSelection;
  }

  static getAllWareClassItem(): apsCommon.IdAndName {
    return CheckListCommon.wareClassAllSelection;
  }
}

export default class CheckListOverrideModule {
  private static getOverrideResult<T>(item: HasOverrides,
                                   prop: (overrideItem: Override) => T)  {
    return (plantId: string,
            shopId: string,
            jobId: string) => {

      let result = this.getOverrideProp(item, prop, null, null, null);
      if (result !== null) {
        return result;
      }

      if (jobId) {
        result = this.getOverrideProp(item, prop, null, null, jobId);
        if (result !== null) {
          return result;
        }
      }

      plantId = plantId || item.plantId;
      shopId = shopId || item.shopId;
      
      if (plantId) {
        result = this.getOverrideProp(item, prop, plantId, null, null);
        if (result !== null) {
          return result;
        }

        if (jobId) {
          result = this.getOverrideProp(item, prop, plantId, null, jobId);
          if (result !== null) {
            return result;
          }
        }

        if (shopId) {
          result = this.getOverrideProp(item, prop, plantId, shopId, null);
          if (result !== null) {
            return result;
          }

          if (jobId) {
            result = this.getOverrideProp(item, prop, plantId, shopId, jobId);
            if (result !== null) {
              return result;
            }
          }
        }
      }

      return null;
    };
  }

  static getOverrideProp<T>(item: HasOverrides,
    prop: (overrideItem: Override) => T,
    plantId: string,
    shopId: string,
    jobId: string) {
      var override = _.find(item.overrides, {plantId: plantId, shopId: shopId, jobId: jobId});
      return override ? prop(override) : null;
  }

  static extendOverrideItem(item: HasOverrides): ExtendedHasOverrides {
    let extendedItem = _.assign<ExtendedHasOverrides>(item, {
      isMandatory: false,
      isDisabled: false
    });
    extendedItem.isMandatoryFor = CheckListOverrideModule.getOverrideResult(extendedItem, (c: Override) => {
      if (c.isMandatory) {
        return true;
      }
      if (c.isDisabled) {
        return false;
      }
      return null;
    });
    extendedItem.isOnlyDisabled = CheckListOverrideModule.getOverrideResult(extendedItem, (c: Override) => {
      if (c.isDisabled) {
        return true;
      }
      return null;
    });
    extendedItem.isDisabledFor = (plantId: string, shopId: string, jobId: string) => {
      return !extendedItem.isMandatoryFor(plantId, shopId, jobId)
        && extendedItem.isOnlyDisabled(plantId, shopId, jobId);
    };
    return extendedItem;
  }

  static setOverrideValue(item: ExtendedHasOverrides,
                          plantId: string, shopId: string, jobId: string,
                          authDataService: AuthDataService): void {
    item.isMandatory = item.isMandatoryFor(plantId, shopId, jobId);
    item.isDisabled = item.isDisabledFor(plantId, shopId, jobId);
    let parentIsMandatory = false;
    let parentIsDisabled = false;
    if (shopId && !item.shopId) {
      parentIsMandatory = item.isMandatoryFor(plantId, null, jobId);
      parentIsDisabled = item.isDisabledFor(plantId, null, jobId);
    } else if (plantId && !item.plantId) {
      parentIsMandatory = item.isMandatoryFor(null, null, jobId);
      parentIsDisabled = item.isDisabledFor(null, null, jobId);
    }

    let currentPlantId = plantId || item.plantId;
    let isAdmin = currentPlantId ? authDataService.isPlantAdmin(currentPlantId) : authDataService.isAdmin();
    item.canChangeMandatory = isAdmin && !parentIsMandatory && !parentIsDisabled;
    item.canChangeDisabled = isAdmin && !parentIsMandatory && !parentIsDisabled
      && !item.isMandatory;
  }

  static setOverrideValues(items: ExtendedHasOverrides[],
                           plantId: string, shopId: string, jobId: string,
                           authDataService: AuthDataService): void {
    _.forEach(items, (item: ExtendedHasOverrides) =>
      CheckListOverrideModule.setOverrideValue(item, plantId, shopId, jobId, authDataService));
  }

  static extendCheckItem(check: apsViewModels.CheckItem): CheckItem {
    return _.assign<CheckItem>({
      isFirst: false,
      isLast: false,
      dataTypeString: apsEnums.CheckDataType[check.dataType],
      entryString: 'Per ' + apsEnums.CheckEntry[check.entry].substring(3),
      isMandatory: false,
      isDisabled: false
    }, CheckListOverrideModule.extendOverrideItem(check));
  }

  static getLocationName(checkList: apsViewModels.CheckListSummary,
                         locations: apsCommon.IdAndName[]) {
    var locationName = null;
    if (checkList.locationType === apsEnums.LocationType.Other) {
      var location = _.find(locations, {id: checkList.locationId});
      if (location) {
        locationName = location.name;
      }
    }
    if (!locationName) {
      locationName = apsEnums.LocationType[checkList.locationType];
    }
    return locationName;
  }

  static getWareClassName(checkList: apsViewModels.CheckListSummary,
                         wareClasses: apsCommon.IdAndName[]) {
    var wareClass = _.find(wareClasses, {id: checkList.wareClassId});
    if (wareClass) {
      return wareClass.name;
    }
    return 'all';
  }

  static extendCheckList(checkList: apsViewModels.CheckListSummary,
                         allCheckLists: apsViewModels.CheckListSummary[],
                         plants: Plant[],
                         shops: apsViewModels.ShopNavigation[],
                         locations: apsCommon.IdAndName[],
                         wareClasses: apsCommon.IdAndName[],
                         plantId: string, shopId: string, jobId: string,
                         authDataService: AuthDataService) {
    var checkListPlant = _.find(plants, {'id': checkList.plantId});
    var checkListShop = _.find(shops, {'id': checkList.shopId});
    var checkListWareClass = _.find(wareClasses, {'id': checkList.wareClassId})

    var plantName = checkListPlant ? checkListPlant.name : 'all';
    var shopName = checkListShop ? checkListShop.name : 'all';
    var wareClass = checkListWareClass ? checkListWareClass.name : 'all';

    var result = _.assign<CheckList>({
      plantName: plantName,
      shopName: shopName,
      wareClass: wareClass,
      canEdit: false,
      locationName: CheckListOverrideModule.getLocationName(checkList, locations)
    }, CheckListOverrideModule.extendOverrideItem(checkList));

    CheckListOverrideModule.setOverrideValue(result, plantId, shopId, jobId, authDataService);
    result.canEdit = authDataService.isAdmin()
      || (checkList.plantId && authDataService.isPlantAdmin(checkList.plantId));
    result.canCopy = !checkList.isGroup;
    if (checkList.checkListGroupId) {
      var group = _.find(allCheckLists, {id: checkList.checkListGroupId});
      if (group) {
        result.checkListGroupName = group.name;
      }
    }
    return result;
  }

  static extendCheckLists(checkLists: apsViewModels.CheckListSummary[],
                          plants: Plant[],
                          shops: apsViewModels.ShopNavigation[],
                          locations: apsCommon.IdAndName[],
                          wareClasses: apsCommon.IdAndName[],
                          plantId: string, shopId: string, jobId: string,
                          authDataService: AuthDataService) {
    var result = <CheckList[]>[];
    _.forEach(checkLists, (checkList: apsViewModels.CheckListSummary) =>
      result.push(CheckListOverrideModule.extendCheckList(checkList, checkLists,
        plants, shops, locations, wareClasses, plantId, shopId, jobId, authDataService))
    );
    return result;
  }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/app/models/checkLists/checkListOverride.module.ts